// GSM closest-match spelling correction for storefront search.
// Dictionary of catalog words lives in assets/gsm-search-terms.json
// (regenerate with scripts/build-search-terms.mjs). Used by
// templates/search.liquid and snippets/collection-search.liquid whenever
// a search is corrected: every typed word is compared against the whole
// catalog dictionary by edit distance (adjacent-swap counts as one edit),
// and words that resemble nothing in the catalog are dropped so one junk
// word can't zero out the whole search. Correctly-spelled words keep their
// own spelling as the leading option but still offer near neighbours, so
// real-word slips (donald -> donalds) can be caught; callers rank the
// resulting variants by how many returned titles actually contain the
// corrected words (see precisionOf).
window.GSMFuzzy = (function () {
  var dictPromise = null;

  function load(url) {
    if (!dictPromise) {
      dictPromise = fetch(url)
        .then(function (r) { return r.json(); })
        .then(function (d) {
          return { words: (d && d.words) || {}, bigrams: (d && d.bigrams) || {} };
        });
    }
    return dictPromise;
  }

  // Damerau-Levenshtein (optimal string alignment), bailing out with -1
  // as soon as the distance must exceed max.
  function editDistance(a, b, max) {
    var al = a.length, bl = b.length;
    if (Math.abs(al - bl) > max) return -1;
    var prevPrev = [], prev = [], cur = [], i, j;
    for (j = 0; j <= bl; j++) prev[j] = j;
    for (i = 1; i <= al; i++) {
      cur = [i];
      var rowMin = i;
      for (j = 1; j <= bl; j++) {
        var cost = a.charAt(i - 1) === b.charAt(j - 1) ? 0 : 1;
        var v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (i > 1 && j > 1 && a.charAt(i - 1) === b.charAt(j - 2) && a.charAt(i - 2) === b.charAt(j - 1)) {
          v = Math.min(v, prevPrev[j - 2] + 1);
        }
        cur[j] = v;
        if (v < rowMin) rowMin = v;
      }
      if (rowMin > max) return -1;
      prevPrev = prev;
      prev = cur;
    }
    return prev[bl] <= max ? prev[bl] : -1;
  }

  // How many edits a word of this length may be from its correction.
  function maxEdits(len) {
    if (len <= 4) return 1;
    if (len <= 6) return 2;
    return 3;
  }

  function logCap(n) {
    return Math.min(Math.log(n + 1) / Math.LN10, 3);
  }

  // Noisy-channel style scoring: fewer edits is better, but how often a
  // word appears in the catalog matters too. Returns the top few candidate
  // corrections as [{word, score, edits}], best first.
  //
  // Edit distance is ranked ahead of the frequency score: a common word two
  // edits away used to bury the rare name one edit away, which is how
  // "bardy" reached for "card" (29,656 uses) instead of "brady" (9) and
  // never even offered it. Frequency now only breaks ties inside an edit
  // tier — deciding between correct spellings, not overruling them.
  function candidatesFor(w, words) {
    var cap = maxEdits(w.length);
    var found = [];
    for (var word in words) {
      if (Math.abs(word.length - w.length) > cap) continue;
      var d = editDistance(w, word, cap);
      if (d < 0) continue;
      found.push({ word: word, score: d - 0.7 * logCap(words[word]), edits: d });
    }
    found.sort(function (a, b) { return a.edits - b.edits || a.score - b.score; });
    return found.slice(0, 4);
  }

  // Builds ranked whole-query variants. Per-word candidates alone can't
  // tell "michael john" from "michael jordan" (john is the more common
  // word), so whole variants are re-scored with a bonus for word pairs
  // that actually appear together in product titles, and the caller tries
  // variants in order until one returns results.
  // Returns {changed, variants: [query, …]} — variants[0] is the best.
  function correctQuery(q, dict) {
    var words = dict.words || {};
    var bigrams = dict.bigrams || {};
    var changed = false;
    var slots = [];

    String(q).trim().split(/\s+/).forEach(function (w) {
      var clean = w.toLowerCase().replace(/[^a-z0-9]/g, '');
      // Keep punctuation-only, tiny, or numeric words exactly as typed.
      if (!clean || clean.length < 2 || /\d/.test(clean)) {
        slots.push([{ word: w, score: 0, edits: 0 }]);
        return;
      }
      // Every word gets a closest-match lookup, including words that already
      // exist in the catalog — "donald" is a real word (Donald Trump) but the
      // shopper may have meant "Donalds". A real word is always kept as its
      // own candidate so a correct spelling can never be scored out entirely.
      var cands = candidatesFor(clean, words);
      if (Object.prototype.hasOwnProperty.call(words, clean)) {
        var self = null;
        for (var ci = 0; ci < cands.length; ci++) {
          if (cands[ci].word === clean) { self = cands[ci]; break; }
        }
        if (!self) cands.unshift({ word: clean, score: -0.7 * logCap(words[clean]), edits: 0 });
      }
      changed = true;
      // No candidate at all → the word resembles nothing in the catalog;
      // drop it so one junk word can't zero out the whole search.
      slots.push(cands.length ? cands : [{ word: null, score: 1, edits: 1 }]);
    });

    if (!changed) return { changed: false, variants: [q], details: [] };

    // Cartesian expansion of per-word candidates, capped for sanity.
    var combos = [[]];
    slots.forEach(function (options) {
      var next = [];
      combos.forEach(function (combo) {
        options.forEach(function (opt) {
          if (next.length < 64) next.push(combo.concat([opt]));
        });
      });
      combos = next;
    });

    var scored = combos.map(function (combo) {
      var total = 0;
      var edits = 0;
      var terms = [];
      combo.forEach(function (opt) {
        total += opt.score;
        edits += opt.edits || 0;
        if (opt.word) terms.push(opt.word);
      });
      for (var i = 0; i + 1 < terms.length; i++) {
        var bf = bigrams[(terms[i] + ' ' + terms[i + 1]).toLowerCase()];
        if (bf) total -= 0.9 * logCap(bf);
      }
      return { query: terms.join(' '), score: total, edits: edits };
    });

    // Same rule for whole-query variants, so the closest spellings survive
    // the cut to four and the bigram bonus in `score` settles ties within a
    // tier ("michael jordan" over "michael gordon").
    scored.sort(function (a, b) { return a.edits - b.edits || a.score - b.score; });

    var seen = {};
    var variants = [];
    var details = [];
    scored.forEach(function (s) {
      var key = s.query.toLowerCase();
      if (!s.query || seen[key] || key === String(q).trim().toLowerCase()) return;
      seen[key] = true;
      if (variants.length < 4) {
        variants.push(s.query);
        details.push({ query: s.query, edits: s.edits, score: s.score });
      }
    });

    return { changed: variants.length > 0, variants: variants, details: details };
  }

  // Shortest typed word allowed to match on prefix alone. Two letters would
  // let "jo" claim every Johnson, Jordan and Jones in the catalog.
  var MIN_PREFIX = 3;

  // Similarity credited to a token that starts with the typed word. Edit
  // similarity tops out at 1 - 1/len (0.75 on a four-letter word), so this
  // sits above every single-edit score and below an exact match.
  var PREFIX_SIM = 0.9;

  // Does a title token satisfy a typed term? An exact token always counts.
  // In `loose` mode a token that STARTS WITH the term counts too — which is
  // the rule Shopify's own search used to build the result set in the first
  // place: it matched "shaq" to "Shaquille" by prefix. A verifier that
  // demands the whole word grades that perfectly good page as a miss.
  function tokenMatches(token, term, loose) {
    if (token === term) return true;
    return !!loose && term.length >= MIN_PREFIX && token.lastIndexOf(term, 0) === 0;
  }

  // How many of `titles` carry every word of `query`?
  // Edit distance alone cannot tell a real correction from a lucky one:
  // "baron donald" returns 8 products and not one of them holds both words,
  // while "byron donalds" returns the real card.
  //
  // Strict (the default) matches whole words only, and that is what judging a
  // CORRECTION CANDIDATE needs: on substrings a truncated word always
  // "verifies" ("jer" sits inside every Jerry Rice title) and the ranking
  // starts rewarding chopping letters off. It also keeps "donald" from
  // quietly passing as "Donalds", which is the whole point of the correction.
  //
  // `loose` is for judging the SHOPPER'S OWN query, where the opposite is
  // true: their words are not guesses, and Shopify already matched them by
  // prefix. Grading "shaq jersey" on whole words scored 0 against 250 real
  // Shaquille O'Neal jerseys and redirected the shopper to "shai jersey".
  function precisionOf(titles, query, loose) {
    var terms = String(query).toLowerCase().split(/[^a-z0-9]+/).filter(function (t) { return t; });
    if (!terms.length || !titles || !titles.length) return 0;
    var n = 0;
    for (var i = 0; i < titles.length; i++) {
      var tokens = String(titles[i] || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
      var all = true;
      for (var j = 0; j < terms.length; j++) {
        var hit = false;
        for (var k = 0; k < tokens.length; k++) {
          if (tokenMatches(tokens[k], terms[j], loose)) { hit = true; break; }
        }
        if (!hit) { all = false; break; }
      }
      if (all) n++;
    }
    return n;
  }

  // The words of `query` the catalog can actually speak to. A token with no
  // neighbour inside the edit radius — a mashed "donalikjfg" — is noise that
  // Shopify's own matching already ignores, and scoring the shopper's results
  // on whether that noise shows up in a title throws away a page that has
  // already found the right product.
  function supportedTerms(query, dict) {
    var words = (dict && dict.words) || {};
    var out = [];
    String(query).trim().split(/\s+/).forEach(function (w) {
      var clean = w.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (!clean) return;
      if (clean.length < 2 || /\d/.test(clean) ||
          Object.prototype.hasOwnProperty.call(words, clean) ||
          candidatesFor(clean, words).length) {
        out.push(clean);
      }
    });
    return out;
  }


  // How closely one product title matches what the shopper typed, comparing
  // word to word rather than demanding exact tokens. This is the ranking the
  // search page sorts by: it is applied to every result the query returned,
  // not just the twenty on screen, so the best match cannot be stranded on
  // page three.
  //
  // Adjacency is measured on the MATCHED positions, not the typed string —
  // that is what separates "Michael Jordan" (words 0,1) from "Michael B.
  // Jordan" (words 0,2) when the shopper typed "micheal jordan" and neither
  // title contains that spelling.
  function titleScore(title, query) {
    var toks = String(title || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    var terms = String(query || '').toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    if (!toks.length || !terms.length) return 0;

    var total = 0, exact = 0, positions = [];
    for (var i = 0; i < terms.length; i++) {
      var q = terms[i], best = 0, bestAt = -1;
      for (var j = 0; j < toks.length; j++) {
        var t = toks[j], sim;
        if (t === q) sim = 1;
        else {
          var longest = Math.max(q.length, t.length);
          // A token that literally starts with the typed word is stronger
          // evidence than one a guessed letter away: Shopify returned
          // "Shaquille" *because* "shaq" is its prefix, while "Shai" is only
          // a substitution. Without this the ranker cannot see "shaq" at all
          // and scores a Shaquille jersey exactly like every other jersey.
          sim = (q.length >= MIN_PREFIX && t.lastIndexOf(q, 0) === 0) ? PREFIX_SIM : 0;
          // Allow roughly one edit per three characters before giving up.
          var d = editDistance(q, t, Math.max(1, Math.floor(longest * 0.34)));
          if (d >= 0) sim = Math.max(sim, 1 - d / longest);
          if (!sim) continue;
        }
        if (sim > best) { best = sim; bestAt = j; }
      }
      total += best;
      if (best === 1) exact++;
      if (bestAt !== -1) positions.push(bestAt);
    }

    var score = (total / terms.length) * 1000;
    if (exact === terms.length) score += 200;

    // Matched words sitting next to each other, in the order typed.
    if (positions.length === terms.length && terms.length > 1) {
      var ordered = true;
      for (var k = 1; k < positions.length; k++) {
        if (positions[k] !== positions[k - 1] + 1) { ordered = false; break; }
      }
      if (ordered) score += 300;
    }
    return score;
  }

  return {
    load: load,
    titleScore: titleScore,
    correctQuery: correctQuery,
    editDistance: editDistance,
    precisionOf: precisionOf,
    supportedTerms: supportedTerms
  };
})();
